import React, { FC } from 'react'
import { HStack, Box, Text, Stack } from '@chakra-ui/react'
import { PaginationItem } from './PaginationItem'
import { number } from 'yup/lib/locale'

interface PaginationProps {
  totalCountOfRegisters: number
  registersPerPage?: number
  currentPage?: number
  onPageChange: (page: number) => void
  totalPages?: number // Add support for direct total pages
}

const siblingsCount = 1

function generatePagesArray (from: number, to: number) {
  return [...new Array(to - from)]
    .map((_, index) => {
      return from + index + 1
    })
    .filter((page) => page > 0)
}

const Pagination: FC<PaginationProps> = ({
  totalCountOfRegisters,
  registersPerPage = 10,
  currentPage = 1,
  onPageChange,
  totalPages
}) => {
  // Use totalPages if provided, otherwise calculate from totalCountOfRegisters
  const lastPage = totalPages || Math.ceil(totalCountOfRegisters / registersPerPage)

  const previusPage =
    currentPage > 1
      ? generatePagesArray(currentPage - 1 - siblingsCount, currentPage - 1)
      : []

  const nextPage =
    currentPage < lastPage
      ? generatePagesArray(
        currentPage,
        Math.min(currentPage + siblingsCount, lastPage)
      )
      : []

  return (
    <Stack
      direction={['column', 'row']}
      mt="8"
      justify="space-between"
      align="center"
      spacing="6"
    >
      <Box>
        {totalPages ? (
          <Text>Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong></Text>
        ) : (
          <>
            <strong>{((currentPage - 1) * registersPerPage) + 1}</strong> - <strong>{Math.min(currentPage * registersPerPage, totalCountOfRegisters)}</strong> de{' '}
            <strong>{totalCountOfRegisters}</strong>
          </>
        )}
      </Box>
      <HStack spacing="2" color="white">
        {currentPage > 1 + siblingsCount && (
          <>
            <PaginationItem onPageChange={onPageChange} number={1} />
            {currentPage > 2 + siblingsCount && (
              <Text color="gray.300" w="8" textAlign="center">
                ...
              </Text>
            )}
          </>
        )}

        {previusPage.length > 0 &&
          previusPage.map((page) => (
            <PaginationItem
              onPageChange={onPageChange}
              key={page}
              number={page}
            />
          ))}

        <PaginationItem
          onPageChange={onPageChange}
          number={currentPage}
          isCurrent
        />

        {nextPage.length > 0 &&
          nextPage.map((page) => (
            <PaginationItem
              onPageChange={onPageChange}
              key={page}
              number={page}
            />
          ))}

        {currentPage + siblingsCount < lastPage && (
          <>
            {currentPage + 1 + siblingsCount < lastPage && (
              <Text color="gray.300" w="8" textAlign="center">
                ...
              </Text>
            )}
            <PaginationItem onPageChange={onPageChange} number={lastPage} />
          </>
        )}
      </HStack>
    </Stack>
  )
}

export { Pagination }
